import React from 'react';
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import {
  HEALTH_CHECK_SCOPE_MODE_LABEL,
  HEALTH_CHECK_SCOPE_MODES,
  HealthCheckScopeMode,
} from '../../Interfaces/IHealthCheckRule';
import { ILegalTransactionType } from '../../Interfaces/ILegalTransactionType';
import {
  HealthCheckSeverity,
  IHealthCheckRuleDefinition,
} from '../../LegalTransaction/HealthCheck/types';
import {
  scopeSelectionSummary,
  selectionCountLabel,
  splitVisibleItems,
} from './ruleScopeSummary';
import {
  HEALTH_CHECK_ROW_GRID,
  SEVERITY_CHIP_COLOR,
  SEVERITY_OPTION_LABEL,
  SEVERITY_SHORT_LABEL,
} from './healthCheckRuleLayout';

/** Der Zustand einer Regel, wie ihn die Einstellungen bearbeiten. */
export interface IRuleDraft {
  isActive: boolean;
  severity: HealthCheckSeverity;
  scopeMode: HealthCheckScopeMode;
  idLegalTransactionTypeArray: number[];
  params: Record<string, number | boolean>;
}

interface IProps {
  definition: IHealthCheckRuleDefinition;
  draft: IRuleDraft;
  /** true, sobald sich der Entwurf vom gespeicherten Zustand unterscheidet. */
  dirty: boolean;
  transactionTypeArray: ILegalTransactionType[];
  onPatch: (patch: Partial<IRuleDraft>) => void;
}

/** Wie viele Rechtsgeschäftsarten als Chip erscheinen, bevor gezählt wird. */
const MAX_VISIBLE_TYPE_CHIPS = 4;

/**
 * Was die beiden Geltungsbereiche fuer kuenftig angelegte Rechtsgeschaeftsarten
 * bedeuten. Stand frueher als Hilfetext unter dem Feld; in der Tabellenzeile
 * ist dafuer kein Platz, die Aussage ist aber wichtig — eine Positivliste
 * erfasst neue Arten eben nicht.
 */
const SCOPE_HINT: Record<'include' | 'exclude', string> = {
  include:
    'Die Prüfung läuft nur bei diesen Rechtsgeschäftsarten. Neu angelegte Arten sind nicht enthalten.',
  exclude:
    'Die Prüfung läuft bei allen Rechtsgeschäftsarten außer diesen. Neu angelegte Arten sind enthalten.',
};

/**
 * Eine Regel als Tabellenzeile (#1283).
 *
 * Die Spalten liegen in einem Grid, dessen Bereiche `HEALTH_CHECK_ROW_GRID`
 * vorgibt — dieselbe Vorgabe nutzt die Kopfzeile der Liste, damit Überschrift
 * und Inhalt zwingend fluchten. Unterhalb der Breakpoint-Grenze stapeln sich
 * die Bedienelemente unter dem Titel, ohne dass die Zeile ein zweites Mal
 * geschrieben werden muss.
 *
 * Die Bedienelemente einer ausgeschalteten Regel bleiben stehen und werden
 * deaktiviert, statt zu verschwinden: sonst bekämen die Spalten Löcher und die
 * Liste verlöre genau die ruhige Ausrichtung, um die es hier geht.
 */
export const HealthCheckRuleRow: React.FC<IProps> = ({
  definition,
  draft,
  dirty,
  transactionTypeArray,
  onPatch,
}) => {
  const selectedTypes = transactionTypeArray.filter(type =>
    draft.idLegalTransactionTypeArray.includes(type.idLegalTransactionType)
  );

  const summary = scopeSelectionSummary(draft.scopeMode, selectedTypes.length);
  const { visible, hiddenCount } = splitVisibleItems(
    selectedTypes,
    MAX_VISIBLE_TYPE_CHIPS
  );

  const paramArray = definition.params ?? [];
  const hasSubRow = summary !== null || paramArray.length > 0;

  const removeType = (idLegalTransactionType: number) =>
    onPatch({
      idLegalTransactionTypeArray: draft.idLegalTransactionTypeArray.filter(
        id => id !== idLegalTransactionType
      ),
    });

  return (
    <Box sx={{ ...HEALTH_CHECK_ROW_GRID, py: 1.25, alignItems: 'start' }}>
      {/* Aktiv */}
      <Box sx={{ gridArea: 'active' }}>
        <Switch
          checked={draft.isActive}
          onChange={(_event, checked) => onPatch({ isActive: checked })}
          inputProps={{ 'aria-label': `${definition.title} aktiv` }}
        />
      </Box>

      {/* Prüfung */}
      <Box sx={{ gridArea: 'rule', minWidth: 0, pt: 0.75 }}>
        <Stack
          direction='row'
          spacing={1}
          alignItems='center'
          sx={{ flexWrap: 'wrap', rowGap: 0.5 }}
        >
          <Typography variant='body2' sx={{ fontWeight: 600 }}>
            {definition.title}
          </Typography>
          <Chip
            size='small'
            variant={draft.isActive ? 'filled' : 'outlined'}
            color={
              draft.isActive ? SEVERITY_CHIP_COLOR[draft.severity] : 'default'
            }
            label={SEVERITY_SHORT_LABEL[draft.severity]}
          />
          {/*
            Gespeichert wird gemeinsam ueber die Kopfleiste. Ohne Markierung an
            der Zeile sieht man nach einem Reiterwechsel nicht mehr, wo etwas
            offen ist.
          */}
          {dirty && (
            <Chip
              size='small'
              variant='outlined'
              color='primary'
              label='geändert'
            />
          )}
        </Stack>

        <Typography variant='caption' color='text.secondary' component='p'>
          {definition.description}
        </Typography>
      </Box>

      {/* Stufe */}
      <Box sx={{ gridArea: 'level', pt: 0.5 }}>
        <Select
          size='small'
          fullWidth
          disabled={!draft.isActive}
          value={draft.severity}
          onChange={event =>
            onPatch({ severity: event.target.value as HealthCheckSeverity })
          }
        >
          {(Object.keys(SEVERITY_OPTION_LABEL) as HealthCheckSeverity[]).map(
            severity => (
              <MenuItem key={severity} value={severity}>
                {SEVERITY_OPTION_LABEL[severity]}
              </MenuItem>
            )
          )}
        </Select>
      </Box>

      {/* Gilt für */}
      <Box sx={{ gridArea: 'scope' }}>
        <RadioGroup
          value={draft.scopeMode}
          onChange={event =>
            onPatch({ scopeMode: event.target.value as HealthCheckScopeMode })
          }
        >
          {HEALTH_CHECK_SCOPE_MODES.map(mode => (
            <FormControlLabel
              key={mode}
              value={mode}
              disabled={!draft.isActive}
              sx={{ my: -0.25 }}
              control={<Radio size='small' sx={{ py: 0.25 }} />}
              label={
                <Typography variant='caption'>
                  {HEALTH_CHECK_SCOPE_MODE_LABEL[mode]}
                </Typography>
              }
            />
          ))}
        </RadioGroup>
      </Box>

      {/* Rechtsgeschäftsarten */}
      <Box sx={{ gridArea: 'types', minWidth: 0, pt: 0.5 }}>
        {draft.scopeMode === 'all' ? (
          <Typography variant='caption' color='text.disabled'>
            –
          </Typography>
        ) : (
          <Tooltip title={SCOPE_HINT[draft.scopeMode]}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              disabled={!draft.isActive}
              size='small'
              fullWidth
              options={transactionTypeArray}
              value={selectedTypes}
              getOptionLabel={type => type.LegalTransactionType}
              isOptionEqualToValue={(option, value) =>
                option.idLegalTransactionType === value.idLegalTransactionType
              }
              onChange={(_event, selected) =>
                onPatch({
                  idLegalTransactionTypeArray: selected.map(
                    type => type.idLegalTransactionType
                  ),
                })
              }
              /* Im geschlossenen Zustand steht nur die Anzahl im Feld. Die Namen
                 vollstaendig zu zeigen wuerde die Spalte je nach Auswahl
                 unterschiedlich breit machen und die Zeilenstruktur zerreissen —
                 nachzulesen sind sie in der Zusatzzeile darunter. */
              renderTags={() => null}
              ListboxProps={{ style: { maxHeight: 260 } }}
              renderOption={(props, type, { selected }) => (
                <li {...props} key={type.idLegalTransactionType}>
                  <Checkbox size='small' checked={selected} sx={{ mr: 1 }} />
                  <ListItemText
                    primaryTypographyProps={{ variant: 'body2' }}
                    primary={type.LegalTransactionType}
                  />
                </li>
              )}
              renderInput={params => (
                <TextField
                  {...params}
                  placeholder={
                    selectionCountLabel(selectedTypes.length) || 'Suchen…'
                  }
                />
              )}
            />
          </Tooltip>
        )}
      </Box>

      {/*
        Zusatzzeile: die gewaehlten Arten zum Nachlesen und Entfernen, sowie die
        Parameter der wenigen Regeln, die welche haben. Sie erscheint nur, wenn
        es etwas zu zeigen gibt, damit die uebrigen Zeilen niedrig bleiben.
      */}
      {hasSubRow && (
        <Box sx={{ gridArea: 'sub', pt: 0.5 }}>
          <Stack
            direction='row'
            spacing={2}
            useFlexGap
            sx={{ flexWrap: 'wrap', alignItems: 'center' }}
          >
            {summary !== null && (
              <Stack
                direction='row'
                spacing={0.75}
                useFlexGap
                sx={{ flexWrap: 'wrap', alignItems: 'center' }}
              >
                <Typography variant='caption' color='text.secondary'>
                  {summary}
                </Typography>
                {visible.map(type => (
                  <Chip
                    key={type.idLegalTransactionType}
                    size='small'
                    variant='outlined'
                    label={type.LegalTransactionType}
                    disabled={!draft.isActive}
                    onDelete={() => removeType(type.idLegalTransactionType)}
                  />
                ))}
                {hiddenCount > 0 && (
                  <Typography variant='caption' color='text.secondary'>
                    +{hiddenCount} weitere
                  </Typography>
                )}
              </Stack>
            )}

            {paramArray.map(param =>
              param.type === 'number' ? (
                <TextField
                  key={param.key}
                  size='small'
                  type='number'
                  label={param.label}
                  helperText={param.helperText}
                  disabled={!draft.isActive}
                  value={draft.params[param.key] ?? ''}
                  onChange={event =>
                    onPatch({
                      params: {
                        ...draft.params,
                        [param.key]: Number(event.target.value),
                      },
                    })
                  }
                  sx={{ maxWidth: 260 }}
                />
              ) : (
                <FormControlLabel
                  key={param.key}
                  disabled={!draft.isActive}
                  control={
                    <Switch
                      size='small'
                      checked={Boolean(draft.params[param.key])}
                      onChange={(_event, checked) =>
                        onPatch({
                          params: { ...draft.params, [param.key]: checked },
                        })
                      }
                    />
                  }
                  label={
                    <Typography variant='caption'>{param.label}</Typography>
                  }
                />
              )
            )}
          </Stack>
        </Box>
      )}
    </Box>
  );
};
